import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildLineImportPayload, type BuildLineImportPayloadInput } from './lineImportStorage'
import { hasInternalApi, internalApiGet, internalApiPost } from './internalApi'

export type SavedLineImport = {
  id: string
  liver_id: string
  liver?: {
    id: string
    display_name: string
    status: string
  } | null
  uploaded_by: string | null
  file_name: string
  source_hash: string
  message_count: number
  active_days: number
  date_start: string | null
  date_end: string | null
  primary_manager_name: string | null
  analysis?: unknown
  handoff_note: string | null
  created_at: string
  updated_at: string
}

export type SavedLineMessage = {
  id: string
  line_import_id: string | null
  liver_id: string | null
  message_index: number | null
  sent_at: string | null
  sent_date: string | null
  sent_time: string | null
  sender: string
  body: string
  message_type: string
  sender_type: string
  sender_manager_id?: string | null
  sender_liver_id?: string | null
  created_at: string
}

export type LineSenderAlias = {
  id: string
  entity_type: 'manager' | 'liver'
  line_name: string
  normalized_line_name: string
  manager_id: string | null
  liver_id: string | null
  status: string
  last_seen_at: string | null
  created_at: string
  updated_at: string
}

export type LineSenderAliasCandidate = {
  id: string
  line_import_id: string
  liver_id: string
  observed_name: string
  normalized_observed_name: string
  suggested_entity_type: 'manager' | 'liver' | null
  suggested_manager_id: string | null
  suggested_liver_id: string | null
  reason: string | null
  confidence: number
  status: 'pending' | 'accepted' | 'rejected' | 'ignored'
  decided_by: string | null
  decided_at: string | null
  created_at: string
  updated_at: string
}

export type CreateLineSenderAliasInput = {
  line_name: string
  entity_type: 'manager' | 'liver'
  manager_id?: string | null
  liver_id?: string | null
}

export type DecideLineSenderAliasCandidateInput = {
  candidateId: string
  action: 'accept' | 'reject'
}

type LineImportsResponse = {
  line_imports: SavedLineImport[]
}

type LineImportDetailResponse = {
  line_import: SavedLineImport
  messages: SavedLineMessage[]
  sender_aliases: LineSenderAlias[]
  alias_candidates: LineSenderAliasCandidate[]
}

type SaveLineImportResponse = {
  line_import: SavedLineImport
  duplicate: boolean
}

type CreateLineSenderAliasResponse = {
  sender_alias: LineSenderAlias
  updated_message_count: number
}

type DecideLineSenderAliasCandidateResponse = {
  alias_candidate: LineSenderAliasCandidate
  sender_alias?: LineSenderAlias
  updated_message_count: number
}

export function useLineImports(liverId: string | undefined) {
  return useQuery<SavedLineImport[]>({
    queryKey: ['line-imports', 'by-liver', liverId],
    enabled: !!liverId && hasInternalApi(),
    queryFn: async () => {
      if (!liverId) return []
      const response = await internalApiGet<LineImportsResponse>(`/api/livers/${liverId}/line-imports`)
      return response.line_imports
    }
  })
}

export function useLineImportDetail(lineImportId: string | undefined) {
  return useQuery<LineImportDetailResponse>({
    queryKey: ['line-imports', 'detail', lineImportId],
    enabled: !!lineImportId && hasInternalApi(),
    queryFn: async () => {
      if (!lineImportId) throw new Error('lineImportId is required')
      return await internalApiGet<LineImportDetailResponse>(`/api/line-imports/${lineImportId}`)
    }
  })
}

export function useCreateLineSenderAlias(lineImportId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateLineSenderAliasInput) => {
      if (!lineImportId) throw new Error('lineImportId is required')
      return await internalApiPost<CreateLineSenderAliasResponse>(
        `/api/line-imports/${lineImportId}/sender-aliases`,
        input
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['line-imports', 'detail', lineImportId] })
    }
  })
}

export function useDecideLineSenderAliasCandidate(lineImportId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: DecideLineSenderAliasCandidateInput) => {
      if (!lineImportId) throw new Error('lineImportId is required')
      return await internalApiPost<DecideLineSenderAliasCandidateResponse>(
        `/api/line-imports/${lineImportId}/alias-candidates/${input.candidateId}`,
        { action: input.action }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['line-imports', 'detail', lineImportId] })
    }
  })
}

export function useSaveLineImport(liverId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: BuildLineImportPayloadInput) => {
      if (!liverId) throw new Error('liverId is required')
      const payload = await buildLineImportPayload(input)
      return await internalApiPost<SaveLineImportResponse>(
        `/api/livers/${liverId}/line-imports`,
        payload
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['line-imports', 'by-liver', liverId] })
    }
  })
}
